import React from 'react';
import { connect } from 'react-redux';
import pt from 'prop-types';

import actions from 'actions';

import { STOREFRONT_DEFAULT_APP } from 'constants/storefront';
import ThemeZipFile from 'utils/theme-zip';

import Modal from 'components/modal';
import { Form, Field } from 'components/form';

export const mapStateToProps = (state) => ({
  client: state.client,
});

export const mapDispatchToProps = (dispatch) => ({
  createThemeApp(data) {
    return dispatch(actions.apps.create(data));
  },

  fetchAllStorefrontShops() {
    return dispatch(actions.apps.fetchAllStorefrontShops());
  },

  installApp(appId, data) {
    return dispatch(actions.client.installApp(appId, data));
  },

  fetchInstalledApps() {
    return dispatch(actions.client.fetchInstalledApps());
  },

  deleteFile(file) {
    return dispatch(actions.files.delete(file));
  },

  createStorefrontWithApp(app, data) {
    return dispatch(actions.storefronts.createWithApp(app, data));
  },
});

export class UploadThemeModal extends React.Component {
  static contextTypes = {
    notifyError: pt.func.isRequired,
    closeModal: pt.func.isRequired,
  };

  static propTypes = {
    client: pt.object,
    createStorefront: pt.bool,

    onUpload: pt.func.isRequired,
    deleteFile: pt.func,
    installApp: pt.func,
    createThemeApp: pt.func,
    fetchInstalledApps: pt.func,
    createStorefrontWithApp: pt.func,
    fetchAllStorefrontShops: pt.func,
  };

  state = {
    themeUploaded: null,
    themeUploading: false,
    themeUploadSuccess: false,
    themeUploadValues: {},
    requiresShopifyImageImport: false,
  };

  onCloseUploadTheme = async (event) => {
    event.preventDefault();
    const { deleteFile } = this.props;
    const { themeUploadSuccess, themeUploaded } = this.state;
    const { closeModal } = this.context;

    if (themeUploaded && !themeUploadSuccess) {
      deleteFile(themeUploaded);
    }

    closeModal();
  };

  onChangeUploadTheme = (values) => {
    this.setState({ themeUploadValues: values });
  };

  onChangeUploadThemeFile = async (_event, file) => {
    const { deleteFile } = this.props;
    const { themeUploaded } = this.state;
    const { notifyError } = this.context;

    if (file) {
      if (themeUploaded) {
        deleteFile(themeUploaded);
      }

      const themeFile = new ThemeZipFile(file.instance);

      const themeFileError = await themeFile.validate();
      if (themeFileError) {
        this.setState({
          themeUploaded: null,
          themeUploadValues: {
            ...this.state.themeUploadValues,
            upload_theme: null,
          },
        });
        notifyError(themeFileError);
        return;
      }

      const requiresShopifyImageImport =
        await themeFile.requiresShopifyImageImport();

      this.setState({ themeUploaded: file, requiresShopifyImageImport });
    }
  };

  onSubmitUploadTheme = async (values) => {
    const { closeModal } = this.context;

    this.setState({ themeUploading: true });

    const success = await this.onUploadTheme(values);

    if (success) {
      closeModal();
    } else {
      this.setState({ themeUploadSuccess: false, themeUploading: false });
    }
  };

  async onUploadTheme(values) {
    const {
      client,
      createStorefront,
      onUpload,
      installApp,
      createThemeApp,
      fetchInstalledApps,
      createStorefrontWithApp,
      fetchAllStorefrontShops,
    } = this.props;
    const { notifyError } = this.context;

    if (!values.upload_theme) {
      notifyError('Please select a file to upload');
      return;
    }

    const result = await createThemeApp({
      upload_theme: values.upload_theme,
      upload_options: values.upload_options,
      // Optionally create storefront while uploading theme
      upload_create_storefront: Boolean(
        createStorefront || values.upload_create_storefront,
      ),
      // Environment specifies where the upload is located
      $environment_id: client.environment_id,
      // Theme name will be reset once it extracts the theme name from swell.json
      name: values.upload_theme.filename || Date.now(),
      type: 'theme',
      theme: {
        storefront: {
          app: STOREFRONT_DEFAULT_APP,
        },
      },
    });

    if (result?.errors) {
      notifyError(result?.errors);
      return;
    }

    if (result) {
      // For the test environment, installation occurs automatically
      if (client.environment_id !== 'test') {
        await (result.upload_create_storefront
          ? fetchAllStorefrontShops().then(() =>
              createStorefrontWithApp(result),
            )
          : installApp(result.id, { version: null }));
      }

      await fetchInstalledApps();
      onUpload(result);
    }

    return result;
  }

  render() {
    const { createStorefront } = this.props;
    const {
      themeUploaded,
      themeUploading,
      themeUploadValues,
      requiresShopifyImageImport,
    } = this.state;

    return (
      <Form
        onChange={this.onChangeUploadTheme}
        onSubmit={this.onSubmitUploadTheme}
      >
        <Modal
          title="Upload theme"
          onClose={this.onCloseUploadTheme}
          width={600}
          actions={[
            {
              label: 'Upload',
              type: 'submit',
            },
          ]}
          loading={themeUploading}
          loadingTitle="Uploading theme"
        >
          <Field
            type="file"
            label="Choose a Swell or Shopify theme to upload (.zip)"
            name="upload_theme"
            accept={['.zip']}
            onChange={this.onChangeUploadThemeFile}
            defaultValue={themeUploadValues.upload_theme}
            required={true}
            sizeType="exceptional"
          />
          {themeUploaded && !createStorefront && (
            <Field
              type="checkbox"
              label="Create a new storefront with this theme"
              name="upload_create_storefront"
              defaultChecked={true}
            />
          )}
          {requiresShopifyImageImport && (
            <>
              <Field
                type="checkbox"
                label="Import images from Shopify"
                name="upload_options.shopify_import_images"
                help="Enter your Shopify store URL to import theme images from your Shopify store. If unchecked, theme images will be removed."
                defaultChecked={true}
              />
              {themeUploadValues.upload_options?.shopify_import_images && (
                <Field
                  type="url"
                  label="Shopify store URL"
                  name="upload_options.shopify_store_url"
                  placeholder="https://your-store.myshopify.com"
                />
              )}
            </>
          )}
        </Modal>
      </Form>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(UploadThemeModal);
